import React, { useState } from 'react';
import {
  ArrowRight,
  Mail,
  Linkedin,
  MapPin,
  Phone,
  Send,
  Users,
  Clock,
  Shield
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const KontaktPage = () => {
  const { t } = useLanguage();

  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    topic: '',
    message: ''
  });

  const topics = Array.isArray(t('contactPage.form.topics'))
    ? t('contactPage.form.topics')
    : [];

  const offices = Array.isArray(t('contactPage.offices'))
    ? t('contactPage.offices')
    : [];

  const heroBullets = Array.isArray(t('contactPage.hero.bullets'))
    ? t('contactPage.hero.bullets')
    : [];

  const whyBullets = Array.isArray(t('contactPage.why.bullets'))
    ? t('contactPage.why.bullets')
    : [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // TODO: Replace with real submission logic / API call
    console.log('Form submitted:', formData);
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-r from-purple-600/30 to-orange-500/30 rounded-full blur-3xl animate-float" />
          <div
            className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-500/30 to-purple-600/30 rounded-full blur-3xl animate-float"
            style={{ animationDelay: '-3s' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-6xl font-light text-white mb-8">
              <span className="gradient-text font-medium">
                {t('contactPage.hero.highlight')}
              </span>
              <br />
              {t('contactPage.hero.titleRest')}
            </h1>

            <p className="text-2xl text-slate-300 max-w-3xl mx-auto leading-relaxed font-light mb-12">
              {t('contactPage.hero.subtitle')}
            </p>

            <div className="flex items-center justify-center flex-wrap gap-8 text-slate-300">
              {heroBullets.map((text, index) => {
                const Icon =
                  index === 0 ? Clock : index === 1 ? Shield : Users;
                return (
                  <div key={index} className="flex items-center">
                    <Icon className="w-5 h-5 mr-2" />
                    <span className="text-sm">{text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-slate-50 rounded-3xl p-8 card-premium">
              <h2 className="text-2xl font-medium text-slate-900 mb-8">
                {t('contactPage.form.titleBeforeHighlight')}{' '}
                <span className="gradient-text">
                  {t('contactPage.form.highlight')}
                </span>
              </h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contactPage.form.nameLabel')} *
                    </label>
                    <input
                      type="text"
                      name="name"
                      required
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      placeholder={t('contactPage.form.namePlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contactPage.form.companyLabel')}
                    </label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      placeholder={t('contactPage.form.companyPlaceholder')}
                    />
                  </div>
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contactPage.form.emailLabel')} *
                    </label>
                    <input
                      type="email"
                      name="email"
                      required
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      placeholder={t('contactPage.form.emailPlaceholder')}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">
                      {t('contactPage.form.phoneLabel')}
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                      placeholder={t('contactPage.form.phonePlaceholder')}
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contactPage.form.topicLabel')}
                  </label>
                  <select
                    name="topic"
                    value={formData.topic}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                  >
                    <option value="">
                      {t('contactPage.form.topicPlaceholder')}
                    </option>
                    {topics.map((topic) => (
                      <option key={topic} value={topic}>
                        {topic}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    {t('contactPage.form.messageLabel')}
                  </label>
                  <textarea
                    name="message"
                    rows={6}
                    value={formData.message}
                    onChange={handleInputChange}
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent transition-colors"
                    placeholder={t('contactPage.form.messagePlaceholder')}
                  />
                </div>

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center px-8 py-4 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium text-lg transition-all duration-300 hover:shadow-glow hover:scale-105"
                >
                  <Send className="w-5 h-5 mr-2" />
                  <span className="mr-2">
                    {t('contactPage.form.submitLabel')}
                  </span>
                  <ArrowRight className="w-5 h-5" />
                </button>

                <p className="text-sm text-slate-500 text-center">
                  {t('contactPage.form.footerText')}
                </p>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div>
                <h2 className="text-2xl font-medium text-slate-900 mb-6">
                  {t('contactPage.contactInfo.titleBeforeHighlight')}{' '}
                  <span className="gradient-text">
                    {t('contactPage.contactInfo.highlight')}
                  </span>
                </h2>

                <div className="space-y-6">
                  <div className="flex items-center p-4 bg-slate-50 rounded-xl">
                    <Mail className="w-6 h-6 text-purple-600 mr-4" />
                    <div>
                      <div className="font-medium text-slate-900">
                        {t('contactPage.contactInfo.emailLabel')}
                      </div>
                      <div className="text-slate-600">
                        {t('contactPage.contactInfo.email')}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-slate-50 rounded-xl">
                    <Linkedin className="w-6 h-6 text-blue-600 mr-4" />
                    <div>
                      <div className="font-medium text-slate-900">
                        {t('contactPage.contactInfo.linkedinLabel')}
                      </div>
                      <div className="text-slate-600">
                        {t('contactPage.contactInfo.linkedin')}
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center p-4 bg-red-50 rounded-xl border border-red-200">
                    <Phone className="w-6 h-6 text-red-600 mr-4" />
                    <div>
                      <div className="font-medium text-slate-900">
                        {t('contactPage.contactInfo.hotlineLabel')}
                      </div>
                      <div className="text-red-600 font-medium">
                        {t('contactPage.contactInfo.hotlineNumber')}
                      </div>
                      <div className="text-sm text-slate-600">
                        {t('contactPage.contactInfo.hotlineNote')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-lg font-medium text-slate-900 mb-4">
                  {t('contactPage.why.title')}
                </h3>
                <ul className="space-y-3 text-slate-600">
                  {whyBullets.map((item, index) => (
                    <li key={index} className="flex items-start">
                      <ArrowRight className="w-4 h-4 text-purple-600 mr-2 mt-1 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      {/*<section className="py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl lg:text-4xl font-light text-white mb-8">
              {t('contactPage.locations.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('contactPage.locations.highlight')}
              </span>
            </h2>
            <p className="text-xl text-slate-300 max-w-2xl mx-auto">
              {t('contactPage.locations.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {offices.map((office, index) => (
              <div
                key={index}
                className="p-8 bg-slate-800/50 backdrop-blur-sm rounded-3xl border border-slate-700/30 card-premium"
              >
                <div
                  className={`inline-flex items-center px-4 py-2 bg-gradient-to-r ${office.gradient} rounded-full text-white text-sm font-medium mb-6`}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  {office.region}
                </div>

                <h3 className="text-xl font-medium text-white mb-6">
                  {office.city}
                </h3>

                <div className="space-y-4 text-slate-300">
                  <div className="flex items-start">
                    <MapPin className="w-4 h-4 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-sm">{office.address}</span>
                  </div>

                  <div className="flex items-center">
                    <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="text-sm">{office.phone}</span>
                  </div>

                  <div className="flex items-center">
                    <Mail className="w-4 h-4 mr-2 flex-shrink-0" />
                    <span className="text-sm">{office.email}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section> */}

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-r from-purple-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-8">
            {t('contactPage.finalCTA.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('contactPage.finalCTA.highlight')}
            </span>
            ?
          </h2>

          <p className="text-xl text-slate-600 leading-relaxed font-light mb-12">
            {t('contactPage.finalCTA.text')}
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-purple-600 to-orange-500 text-white rounded-full font-medium transition-all duration-300 hover:shadow-glow hover:scale-105">
              <span className="mr-2">
                {t('contactPage.finalCTA.primaryButton')}
              </span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button className="inline-flex items-center px-8 py-4 border-2 border-purple-600 text-purple-600 rounded-full font-medium transition-all duration-300 hover:bg-purple-600 hover:text-white">
              <span className="mr-2">
                {t('contactPage.finalCTA.secondaryButton')}
              </span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
